import type { TwoFactorCodeKind, TwoFactorCodeKindConfig } from './types';

export const LOGIN_FIELDS = {
  EMAIL: 'email',
  PASSWORD: 'password',
  REMEMBER_ME: 'rememberMe'
} as const;

/** Validation error codes for the login form from server response */
export const LOGIN_VALIDATION_ERROR_CODES = {
  REQUIRED: 'required',
  INVALID_FORMAT: 'invalid_format',
  MAX_LENGTH: 'max_length',
  MIN_LENGTH: 'min_length'
} as const;

/** 401 error codes returned by the login API that need dedicated handling. */
export const LOGIN_UNAUTHORIZED_ERROR_CODES = {
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  PASSWORD_RESET_REQUIRED: 'PASSWORD_RESET_REQUIRED'
} as const;

/** Tracking configuration for the login flow */
export const LOGIN_TRACKING_CONFIG = {
  eventPrefix: 'login',
  eventCategory: 'login'
} as const;

/** Tracking configuration for the email/password login form */
export const LOGIN_FORM_TRACKING_CONFIG = {
  eventPrefix: 'login',
  eventCategory: 'login',
  eventLabel: 'email'
} as const;

/** reCAPTCHA Enterprise action label for the login form. */
export const LOGIN_RECAPTCHA_ACTION = 'login' as const;

/** Server error field key for the login reCAPTCHA token (camelCased from `g_recaptcha_response`). */
export const LOGIN_RECAPTCHA_FIELD = 'gRecaptchaResponse' as const;

/** Field names for the two-factor code form. */
export const TWO_FACTOR_FIELDS = {
  CODE: 'code'
} as const;

/** The kinds of code the 2FA step takes. */
export const TWO_FACTOR_CODE_KIND = {
  APP: 'app',
  BACKUP: 'backup'
} as const;

/** What the 2FA step shows for each kind of code, and the kind its switch leads to. */
export const TWO_FACTOR_CODE_KINDS = {
  [TWO_FACTOR_CODE_KIND.APP]: {
    canTakeLetters: false,
    descriptionKey: 'auth.login.twoFactor.description',
    labelKey: 'auth.login.twoFactor.fields.code',
    switchLabelKey: 'auth.login.twoFactor.useBackupCode',
    switchTo: TWO_FACTOR_CODE_KIND.BACKUP
  },
  [TWO_FACTOR_CODE_KIND.BACKUP]: {
    canTakeLetters: true,
    descriptionKey: 'auth.login.twoFactor.backupCodeDescription',
    labelKey: 'auth.login.twoFactor.fields.backupCode',
    switchLabelKey: 'auth.login.twoFactor.useAppCode',
    switchTo: TWO_FACTOR_CODE_KIND.APP
  }
} as const satisfies Record<TwoFactorCodeKind, TwoFactorCodeKindConfig>;

/** Letters and digits only. The length is checked on its own, against `TWO_FACTOR_CODE_LENGTH`. */
export const TWO_FACTOR_CODE_PATTERN = /^[0-9A-Za-z]+$/;
